/*
 * dashcam/mux.js — one playable MP4 out of a stretch of the loop.
 *
 * The frames were encoded once, while driving; saving an event or the last
 * few minutes never re-encodes them. The records of the window are read back
 * from the store segment by segment (one read per segment, never the whole
 * loop at once) and handed to mp4-muxer as they are, so saving a 90-second
 * event costs a few megabytes of reading and no picture quality.
 *
 * The file goes to the store through positional writes with the moov reserved
 * up front (fastStart with the exact sample counts, which we know): it plays
 * from the first byte, on a phone, in a messenger preview, in Windows «Movies
 * & TV», and it never sits whole in the page's memory.
 *
 * buildWebm — the same for a loop of VP9/AV1 + Opus (the screen recorder's
 * instant replay where the browser has no H.264 encoder): a clip keeps the
 * container of the take it came from.
 */

import { HDR, KIND, isVideo } from './ring.js';

/** How much the file will take, for the storage check before writing it. */
export function estimateBytes(win) {
    let b = 4096;
    for (const r of win.video) b += r.size + 24;
    for (const r of win.audio) b += r.size + 16;
    return b;
}

/**
 * store    — see store.js: the loop's segments are read from it;
 * outStore — where outName is created, written and closed (default: store) —
 *            the page falls back to memory when the disk refuses the file.
 * win    — RingIndex.window(a, b)
 * cfg    — {video:{codec:'avc'|'vp9', codecString, width, height, fps, description?},
 *           audio?:{codec:'aac'|'opus', codecString, sampleRate, channels, description?}}
 * Muxer  — the Mp4Muxer namespace (window.Mp4Muxer in the page).
 * → {bytes, durationUs, videoChunks, audioChunks}
 */
export async function buildMp4({ store, outStore, win, cfg, Muxer, outName, onProgress }) {
    const out = outStore || store;
    if (!win || !win.video.length) throw new Error('dashcam: nothing to save');
    if (!Muxer || typeof Muxer.Muxer !== 'function') throw new Error('dashcam: mp4 muxer is not loaded');
    const withAudio = !!(cfg.audio && win.audio.length);

    await out.create(outName);
    let written = 0;
    const target = new Muxer.StreamTarget({
        onData: (data, position) => {
            // the muxer may reuse `data` after this returns — the store copies
            out.writeAt(outName, data, position);
            if (position + data.byteLength > written) written = position + data.byteLength;
        },
    });
    const opts = {
        target,
        video: { codec: cfg.video.codec, width: cfg.video.width, height: cfg.video.height, frameRate: cfg.video.fps || 30 },
        fastStart: { expectedVideoChunks: win.video.length, expectedAudioChunks: withAudio ? win.audio.length : 0 },
        firstTimestampBehavior: 'cross-track-offset',
    };
    if (withAudio) opts.audio = { codec: cfg.audio.codec, numberOfChannels: cfg.audio.channels || 1, sampleRate: cfg.audio.sampleRate };
    const muxer = new Muxer.Muxer(opts);

    let firstV = true, firstA = true;
    const vMeta = { decoderConfig: { codec: cfg.video.codecString, codedWidth: cfg.video.width, codedHeight: cfg.video.height } };
    if (cfg.video.description) vMeta.decoderConfig.description = cfg.video.description;
    const aMeta = withAudio ? { decoderConfig: { codec: cfg.audio.codecString, sampleRate: cfg.audio.sampleRate, numberOfChannels: cfg.audio.channels || 1 } } : null;
    if (aMeta && cfg.audio.description) aMeta.decoderConfig.description = cfg.audio.description;
    await walkWindow(store, win, withAudio, (r, data) => {
        if (isVideo(r.kind)) {
            muxer.addVideoChunkRaw(data, r.kind === KIND.KEY ? 'key' : 'delta', r.ts, r.dur, firstV ? vMeta : undefined);
            firstV = false;
        } else {
            muxer.addAudioChunkRaw(data, 'key', r.ts, r.dur, firstA ? aMeta : undefined);
            firstA = false;
        }
    }, onProgress);
    muxer.finalize();
    const size = await out.close(outName);
    return {
        bytes: size > 0 ? size : written,
        durationUs: win.t1 - win.t0,
        videoChunks: win.video.length,
        audioChunks: withAudio ? win.audio.length : 0,
    };
}

/**
 * The records of a window, read back segment by segment (one read per
 * segment, never the whole loop at once), in file order → onRec(rec, bytes).
 */
async function walkWindow(store, win, withAudio, onRec, onProgress) {
    const bySeg = new Map();
    const keep = new Set(win.video);
    if (withAudio) for (const r of win.audio) keep.add(r);
    for (const r of keep) {
        let list = bySeg.get(r.seg);
        if (!list) bySeg.set(r.seg, list = []);
        list.push(r);
    }
    const segs = [...bySeg.keys()].sort((a, b) => a.t0 - b.t0);
    const total = keep.size;
    let done = 0;
    for (const seg of segs) {
        const list = bySeg.get(seg).sort((a, b) => a.off - b.off);
        const from = list[0].off;
        const last = list[list.length - 1];
        const bytes = await store.read(seg.name, from, last.off + HDR + last.size - from);
        for (const r of list) {
            const at = r.off - from + HDR;
            if (at + r.size > bytes.byteLength) throw new Error('dashcam: a segment is shorter than its index (' + seg.name + ')');
            onRec(r, bytes.subarray(at, at + r.size));
            done++;
        }
        if (onProgress) { try { onProgress(done / total); } catch (_) {} }
    }
}

/**
 * The same stretch as ONE WebM — for a loop whose encoder made VP9 or AV1 with
 * Opus: the screen recorder's lane in a browser without an H.264 encoder
 * (Yandex Browser among them), whose instant-replay clip keeps the container
 * of the take it came from. webm-muxer writes the Cues after the clusters and
 * patches sizes by position — the store's writeAt takes both. Both tracks are
 * rebased to the window's first picture with ONE offset ('permissive': the
 * first sound may begin a little after it, and a per-track offset would move
 * it to zero — out of sync).
 * cfg.video.codec 'vp9' | 'av1', cfg.audio.codec 'opus'; Muxer — WebMMuxer.
 * → {bytes, durationUs, videoChunks, audioChunks}
 */
/** OpusHead: version 1, channels, pre-skip 312 (libopus at 48 kHz), input rate, gain 0, mapping family 0. */
export function opusHead(channels, inputRate) {
    const b = new Uint8Array(19);
    b.set([0x4F, 0x70, 0x75, 0x73, 0x48, 0x65, 0x61, 0x64], 0);   // "OpusHead"
    const dv = new DataView(b.buffer);
    dv.setUint8(8, 1);
    dv.setUint8(9, channels);
    dv.setUint16(10, 312, true);
    dv.setUint32(12, inputRate, true);
    dv.setInt16(16, 0, true);
    dv.setUint8(18, 0);
    return b;
}

export async function buildWebm({ store, outStore, win, cfg, Muxer, outName, onProgress }) {
    const out = outStore || store;
    if (!win || !win.video.length) throw new Error('loop: nothing to save');
    if (!Muxer || typeof Muxer.Muxer !== 'function') throw new Error('loop: webm muxer is not loaded');
    const withAudio = !!(cfg.audio && win.audio.length);
    await out.create(outName);
    let written = 0;
    const target = new Muxer.StreamTarget({
        onData: (data, position) => {
            out.writeAt(outName, data, position);
            if (position + data.byteLength > written) written = position + data.byteLength;
        },
    });
    const opts = {
        target,
        video: { codec: cfg.video.codec === 'av1' ? 'V_AV1' : 'V_VP9', width: cfg.video.width, height: cfg.video.height, frameRate: cfg.video.fps || 30 },
        firstTimestampBehavior: 'permissive',
    };
    if (withAudio) opts.audio = { codec: 'A_OPUS', numberOfChannels: cfg.audio.channels || 1, sampleRate: cfg.audio.sampleRate };
    const muxer = new Muxer.Muxer(opts);
    const t0 = win.t0;
    let firstV = true, firstA = true;
    const vMeta = { decoderConfig: { codec: cfg.video.codecString, codedWidth: cfg.video.width, codedHeight: cfg.video.height } };
    if (cfg.video.description) vMeta.decoderConfig.description = cfg.video.description;
    // Matroska carries Opus's identification header as CodecPrivate; webm-muxer
    // writes it from the description and cannot go without one. The encoder's own
    // header when it gave one, else the standard one (RFC 7845 §5.1).
    const aMeta = withAudio ? { decoderConfig: { codec: cfg.audio.codecString, sampleRate: cfg.audio.sampleRate, numberOfChannels: cfg.audio.channels || 1,
        description: cfg.audio.description || opusHead(cfg.audio.channels || 1, cfg.audio.sampleRate || 48000) } } : null;
    await walkWindow(store, win, withAudio, (r, data) => {
        const ts = Math.max(0, r.ts - t0);
        if (isVideo(r.kind)) {
            muxer.addVideoChunkRaw(data, r.kind === KIND.KEY ? 'key' : 'delta', ts, firstV ? vMeta : undefined);
            firstV = false;
        } else {
            muxer.addAudioChunkRaw(data, 'key', ts, firstA ? aMeta : undefined);
            firstA = false;
        }
    }, onProgress);
    muxer.finalize();
    const size = await out.close(outName);
    return {
        bytes: size > 0 ? size : written,
        durationUs: win.t1 - win.t0,
        videoChunks: win.video.length,
        audioChunks: withAudio ? win.audio.length : 0,
    };
}
